/**
 * Frame Utilities for Effect Rendering
 *
 * Pure utility functions for effect rendering calculations.
 * These functions are injected into effect render methods to provide
 * common positioning and range calculations.
 */

import { roundSubPixel } from './utils.js';

/**
 * Resolves a named point into an absolute numeric X position, offset by the current track position.
 *
 * Supports infinite sentinels:
 * - `"L+"` → `-Infinity`
 * - `"R+"` → `Infinity`
 *
 * @param {string} pointName - Point key (e.g. `"L1"`, `"C"`, `"R+"`)
 * @param {number} trackPosition - Current track offset in px
 * @param {Object} transformPoints - Named position points from frame
 * @returns {number} Absolute position in px
 * @throws {Error} If pointName is unknown and not an infinity keyword
 */
export function getPointValue(pointName, trackPosition, transformPoints) {
	// Handle infinite sentinels
	if (pointName === 'L+') return Number.NEGATIVE_INFINITY;
	if (pointName === 'R+') return Number.POSITIVE_INFINITY;

	// Get base point value
	const base = transformPoints?.[pointName];
	if (base == null) {
		throw new Error(
			`getPointValue: unknown point "${pointName}". Available points: ${Object.keys(transformPoints || {}).join(', ')}`
		);
	}

	return base + trackPosition;
}

/**
 * Computes a normalized range between two named points, with start ≥ end.
 *
 * @param {string} pointNameA - First point name
 * @param {string} pointNameB - Second point name
 * @param {number} trackPosition - Current track offset in px
 * @param {Object} transformPoints - Named position points from frame
 * @returns {{ start: number, end: number }} Range in px, offset for the current track position
 */
export function getRange(pointNameA, pointNameB, trackPosition, transformPoints) {
	const a = getPointValue(pointNameA, trackPosition, transformPoints);
	const b = getPointValue(pointNameB, trackPosition, transformPoints);
	return a > b ? { start: a, end: b } : { start: b, end: a };
}

/**
 * Checks if a slide's center is within the given point range and calculates its normalized position.
 *
 * @param {HTMLElement} slide - Slide element with centerPoint property
 * @param {string} pointNameA - First point name
 * @param {string} pointNameB - Second point name
 * @param {number} trackPosition - Current track offset in px
 * @param {Object} transformPoints - Named position points from frame
 * @returns {{
 *   isInRange: boolean,
 *   percent: number,
 *   start: number,
 *   end: number
 * }} Range check result
 */
export function isSlideInRange(slide, pointNameA, pointNameB, trackPosition, transformPoints) {
	const { start, end } = getRange(pointNameA, pointNameB, trackPosition, transformPoints);
	const roundedStart = roundSubPixel(start);
	const roundedEnd = roundSubPixel(end);
	const roundedCenter = roundSubPixel(slide._centerPoint);

	const isInRange = roundedCenter <= roundedStart && roundedCenter > roundedEnd;

	let percent = 0;
	if (isInRange) {
		if (Number.isFinite(roundedStart) && Number.isFinite(roundedEnd)) {
			const full = roundedStart - roundedEnd;
			// Avoid division by zero, although range rule should prevent this
			percent = full > 0 ? Math.max(0, Math.min(1, (roundedCenter - roundedEnd) / full)) : 1;
		} else {
			// For infinite ranges, percent is 1 if in range, 0 otherwise.
			percent = 1;
		}
	}

	return { isInRange, percent, start: roundedStart, end: roundedEnd };
}

/**
 * Creates a frame utilities object with pre-bound transform points and track position.
 * This provides a cleaner API for effects to use.
 *
 * @param {Object} frame - Complete frame object
 * @returns {Object} Utilities object with bound helper functions
 */
export function createFrameUtils(frame) {
	const { animation, transformPoints } = frame;
	const trackPosition = animation.trackPosition === 0 ? 0 : -animation.trackPosition;

	return {
		/**
		 * Get absolute position for a named point
		 * @param {string} pointName - Point name (e.g. "L1", "C", "R2")
		 * @returns {number} Absolute position in px
		 */
		getPointValue: (pointName) => getPointValue(pointName, trackPosition, transformPoints),

		/**
		 * Get range between two named points
		 * @param {string} pointNameA - First point name
		 * @param {string} pointNameB - Second point name
		 * @returns {{ start: number, end: number }} Range object
		 */
		getRange: (pointNameA, pointNameB) =>
			getRange(pointNameA, pointNameB, trackPosition, transformPoints),

		/**
		 * Check if slide is in range and get progress
		 * @param {HTMLElement} slide - Slide element
		 * @param {string} pointNameA - First point name
		 * @param {string} pointNameB - Second point name
		 * @returns {{ isInRange: boolean, percent: number, start: number, end: number }}
		 */
		isSlideInRange: (slide, pointNameA, pointNameB) =>
			isSlideInRange(slide, pointNameA, pointNameB, trackPosition, transformPoints),

		// Direct access to frame data for convenience
		trackPosition,
		transformPoints,
		frame,
	};
}
